import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../app/hooks";
import {
	clearSchool,
	setDistrict,
	setSchool,
	setSchools,
	transitionSchool,
} from "../../../app/userSlice";
import NamedEntity from "../../../interfaces/NamedEntity";
import {
	useGetSchoolPickerOptionsQuery,
	useMakeSchoolSelectionMutation,
} from "../../schools/schoolsApiSlice";
import Picker from "./Picker";

function schoolsKey(schools?: NamedEntity[]) {
	if (!schools) return "";
	return schools.map((s) => `${s.id}:${s.name}`).join("|");
}

export default function SchoolPicker() {
	const { school } = useAppSelector((state) => state.user);
	const selectSchoolURL = "/selectSchool";

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const schoolsResult = useGetSchoolPickerOptionsQuery();
	const schools = schoolsResult.data;

	const [callSelectSchool] = useMakeSchoolSelectionMutation();

	// Guard against dispatching setSchools whenever RTK Query returns a new
	// array reference with the same underlying data. Without this guard, this
	// effect (combined with cross-tab redux-state-sync broadcasting) can create
	// a rapid dispatch loop between open tabs, freezing the UI.
	const lastSchoolsKey = useRef<string>();
	useEffect(() => {
		const key = schoolsKey(schools);
		if (schools && key !== lastSchoolsKey.current) {
			lastSchoolsKey.current = key;
			dispatch(setSchools(schools));
		}
	}, [schools]);

	function handleSelectSchool(school: NamedEntity) {
		dispatch(transitionSchool());
		callSelectSchool(school.id)
			.unwrap()
			.then((district) => {
				dispatch(
					setSchool({
						id: school.id,
						name: school.name,
					})
				);
				dispatch(setDistrict(district ? { id: district.id, name: district.name } : null));
				navigate("/adminHome");
			})
			.catch((error) => {
				dispatch(clearSchool());
				navigate(selectSchoolURL);
			});
	}

	if (!schools || !school) return null;

	return (
		<Picker
			currentOption={school}
			options={schools}
			label={"schools"}
			navigateURL={selectSchoolURL}
			onClick={handleSelectSchool}
		/>
	);
}
